import React, {
  useEffect, useState, useContext,
} from 'react';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';

import { useTranslation } from 'react-i18next';

import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';

// Provider
import ModeContext from '../../context/ModeContext';
import UserContext from '../../context/UserContext';

// components
import WaterMap from './map/Map';
import WLDataLists from './WLlists/waterLevelLists/WLDataLists';
import WaterVolumeLists from './WLlists/waterVolumeList/WaterVolumeLists';
import WLGateLists from './WLlists/waterGateLists/WLGateDataLists';
import WLCctvLists from './WLlists/cctvLists/WLCctvDataLists';
import WlLegend from './legend/WlLegend';

import { WaterLevelIcon } from '../../assets/svg/Icon';

// style
import './styles.css';
import { globalStyle } from '../../global/style';
import style from './WLlists/style';

const useStyles = makeStyles(globalStyle);
const useStylesTab = makeStyles(style);

function TabPanel(props) {
  const {
    children, value, index,
  } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}
const TabContainer = ({ children }) => (
  <Box component="div">
    {children}
  </Box>
);

TabContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `water-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function Main() {
  let agency;

  const { t } = useTranslation();
  const classes = useStyles();
  const classesTab = useStylesTab();
  const [error, setError] = useState('');
  const [isLoading, setLoading] = useState(true);

  const [dataWaterLevel, setDataWaterLevel] = useState([]);
  const [dataWaterVolume, setDataWaterVolume] = useState([]);
  const [dataWaterGate, setDataWaterGate] = useState([]);
  const [dataCctv, setDataCctv] = useState([]);

  const [dataWaterLevelCurrent, setDataWaterLevelCurrent] = useState([]);
  const [dataWaterVolumeCurrent, setDataWaterVolumeCurrent] = useState([]);
  const [dataWaterGateCurrent, setDataWaterGateCurrent] = useState([]);
  const [dataCctvCurrent, setDataCctvCurrent] = useState([]);

  const [map, setMap] = useState(null);
  const [markers] = useState([]);
  const [value, setValue] = useState(0);
  const [type, setType] = useState('waterlevel');

  const { currentMode, currentArea } = useContext(ModeContext);
  const { userIsLogin } = useContext(UserContext);

  function handleSetCurrentData(data) {
    if (type === 'waterlevel') {
      setDataWaterLevelCurrent(data);
    } else if (type === 'waterVolume') {
      setDataWaterVolumeCurrent(data);
    } else if (type === 'watergate') {
      setDataWaterGateCurrent(data);
    } else if (type === 'cctv') {
      setDataCctvCurrent(data);
    }
  }

  function handleData() {
    setType('waterlevel');
  }

  function handleDataWaterGate() {
    setType('watergate');
  }

  function handleDataCctv() {
    setType('cctv');
  }

  // get map reference and set to state
  const handleSetMap = (mapRef) => {
    setMap(mapRef);
  };

  // link table to marker
  const handleSearchMarker = (id, lat, lng) => (e) => {
    // prevent default action
    e.preventDefault();

    // pan map to current marker position
    map.panTo([parseFloat(lat), parseFloat(lng)]);

    // open Popup
    const currentMarker = markers[id];
    if (currentMarker) {
      currentMarker.openPopup();
    }
  };

  const handleChange = (event, newValue) => {
    if (newValue === 0) setType('waterlevel');
    if (newValue === 1) setType('waterVolume');
    if (newValue === 2) setType('watergate');
    if (newValue === 3) setType('cctv');

    setValue(newValue);
  };

  function calculateCapacitySituation(percentage) {
    if (percentage > 100) {
      return 5;
    } if (percentage > 70) {
      return 4;
    } if (percentage > 30) {
      return 3;
    } if (percentage > 10) {
      return 2;
    } if (percentage <= 10) {
      return 1;
    }
    return 1;
  }

  useEffect(() => {
    const fetchData = async () => {
      const currentDateTime = new Date();
      const last24hrs = currentDateTime.setDate(currentDateTime.getDate() - 2);
      // ระดับน้ำในแม่น้ำ
      fetch(`${process.env.REACT_APP_API_URL}/public/waterlevel_load`)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Something went wrong ...');
        })
        .then((result) => {
          const filterWaterLevel = orderBy(result.waterlevel_data.data, {
            storage_percent: Number,
          })
            .filter((d) => {
              const time = new Date(d.waterlevel_datetime.replace(/-/g, '/')).getTime();
              return last24hrs < time && d.waterlevel_msl != null;
            })
            .filter((d) => d.storage_percent != null);

          setDataWaterLevel(filterWaterLevel);
          setDataWaterLevelCurrent(filterWaterLevel);
          const filterWaterVolume = filterWaterLevel.map((d) => {
            const newData = d;
            newData.volumeCapacity = (d.discharge / d.station.qmax) * 100;
            newData.volumeSituation = calculateCapacitySituation(newData.volumeCapacity);
            return newData;
          }).filter((d) => d.station.qmax != null);
          setDataWaterVolume(orderBy(filterWaterVolume, ['volumeCapacity'], ['desc']));
          setDataWaterVolumeCurrent(orderBy(filterWaterVolume, ['volumeCapacity'], ['desc']));
        })
        .catch((err) => setError(err));
      // ปตร.
      fetch(`${process.env.REACT_APP_API_URL}/public/watergate_load`)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Something went wrong ...');
        })
        .then((result) => {
          const currentDateTimeWaterGate = new Date();
          const last24hrsWaterGate = currentDateTime
            .setDate(currentDateTimeWaterGate.getDate() - 1);
          const filterDatas = result.watergate_data.data.filter((d) => {
            const time = new Date(d.watergate_datetime_in.replace(/-/g, '/')).getTime();
            return last24hrsWaterGate < time && d.watergate_in != null;
          });
          setDataWaterGate(filterDatas);
          setDataWaterGateCurrent(filterDatas);
        })
        .catch((err) => setError(err));

      // cctv
      fetch(`${process.env.REACT_APP_API_URL}/analyst/cctv`)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Something went wrong ...');
        })
        .then((result) => {
          const cctv = result.data.filter((row) => row.media_type === 'img' && row.is_active === true);
          setDataCctv(cctv);
          setDataCctvCurrent(cctv);
        })
        .catch((err) => setError(err));
      setLoading(false);
    };

    fetchData();
  }, []);

  if (type === 'waterlevel' && dataWaterLevel) {
    agency = [...new Set(
      dataWaterLevel
        .map((d) => (d.agency.agency_name.th)),
    )].filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'th')).join(', ');
  } else if (type === 'waterVolume' && dataWaterLevel) {
    agency = [...new Set(
      dataWaterLevel
        .map((d) => (d.agency.agency_name.th)),
    )].filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'th')).join(', ');
  } else if (type === 'watergate' && dataWaterGate) {
    agency = [...new Set(
      dataWaterGate
        .map((d) => (d.agency.agency_name.th)),
    )].filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'th')).join(', ');
  } else if (type === 'cctv' && dataCctv) {
    agency = [...new Set(
      dataCctv
        .map((d) => (d.agency.agency_name.th)),
    )].filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'th')).join(', ');
  }

  return (
    <div className={classes.root}>
      {error && <div>Error Occured</div>}
      {isLoading && (
        <div className="Loading">
          <CircularProgress />
        </div>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={4}>
          <Box display="flex" alignItems="center">
            <Box display="flex" className={classes.shapeCircle} alignItems="center" justifyContent="center">
              <WaterLevelIcon style={{ color: 'white' }} />
            </Box>
            <Box p={1}><Typography variant="h6" className={classes.titlePage}>{t('ระดับน้ำ')}</Typography></Box>
          </Box>
          <Box>
            <WaterMap
              markers={markers}
              dataWaterLevel={dataWaterLevelCurrent}
              dataWaterVolume={dataWaterVolumeCurrent}
              dataWateGate={dataWaterGateCurrent}
              dataCctv={dataCctvCurrent}
              type={type}
              handleSetMap={handleSetMap}
              currentMode={currentMode}
              currentArea={currentArea}
            />
            {(type === 'waterlevel' || type === 'waterVolume') && (<WlLegend />)}
            {t('ที่มา')}
            {' '}
            :
            {' '}
            {agency}
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={8}>
          <Box mt={6}>
            <Tabs value={value} onChange={handleChange} indicatorColor="primary" scrollButtons="auto">
              <Tab label={t('ระดับน้ำในแม่น้ำ')} {...a11yProps(0)} className={value === 0 ? classesTab.active_tab : classesTab.tab} />
              <Tab label={t('ปริมาณน้ำท่า')} {...a11yProps(1)} className={value === 1 ? classesTab.active_tab : classesTab.tab} />
              <Tab label={t('ระดับน้ำ ปตร./ฝาย')} {...a11yProps(2)} className={value === 2 ? classesTab.active_tab : classesTab.tab} />
              <Tab label={t('กล้อง CCTV')} {...a11yProps(3)} className={value === 3 ? classesTab.active_tab : classesTab.tab} />
            </Tabs>
            <TabContainer component="div">
              <TabPanel value={value} index={0}>
                <WLDataLists
                  datas={dataWaterLevel}
                  handleSearchMarker={handleSearchMarker}
                  handleData={handleData}
                  handleSetCurrentData={handleSetCurrentData}
                  currentMode={currentMode}
                  currentArea={currentArea}
                  userIsLogin={userIsLogin}
                />
              </TabPanel>
            </TabContainer>
            <TabContainer component="div">
              <TabPanel value={value} index={1}>
                <WaterVolumeLists
                  datas={dataWaterVolume}
                  handleSearchMarker={handleSearchMarker}
                  handleData={handleData}
                  handleSetCurrentData={handleSetCurrentData}
                  currentMode={currentMode}
                  currentArea={currentArea}
                  userIsLogin={userIsLogin}
                />
              </TabPanel>
            </TabContainer>
            <TabContainer component="div">
              <TabPanel value={value} index={2}>
                <WLGateLists
                  datas={dataWaterGate}
                  handleSearchMarker={handleSearchMarker}
                  handleData={handleDataWaterGate}
                  handleSetCurrentData={handleSetCurrentData}
                  currentMode={currentMode}
                  currentArea={currentArea}
                  userIsLogin={userIsLogin}
                />
              </TabPanel>
            </TabContainer>
            <TabContainer component="div">
              <TabPanel value={value} index={3}>
                <WLCctvLists
                  datas={dataCctv}
                  handleSearchMarker={handleSearchMarker}
                  handleData={handleDataCctv}
                  handleSetCurrentData={handleSetCurrentData}
                  currentMode={currentMode}
                  currentArea={currentArea}
                  userIsLogin={userIsLogin}
                />
              </TabPanel>
            </TabContainer>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
}

TabPanel.defaultProps = {
  children: '',
};
